/**
 * maps-url.js — ENLACE DE GOOGLE MAPS del bloque `maps`: qué pegó el cliente → qué se incrusta.
 *
 * POR QUÉ EXISTE: el bloque solo sabía buscar la DIRECCIÓN escrita (`maps.google.com/maps?q=<dir>`).
 * Una dirección no es un lugar: en un polígono, un centro comercial o una calle larga la chincheta
 * cae donde Google interpreta el texto, no en el negocio, y el botón «Abrir en Google Maps» abre una
 * BÚSQUEDA en vez de la ficha (con sus reseñas, horario y «Cómo llegar»). Lo que el cliente tiene a
 * mano es el enlace de su ficha: botón «Compartir» de Google Maps, en el móvil o en el ordenador.
 *
 * MÓDULO PURO: no toca DOM ni `window` (ejercitable en node — ver tests/_maps_url.mjs). Lo usan el
 * panel del editor (editor/maps-panel.js), que guarda el resultado en los props, y el renderer
 * (blocks/maps.js), que VUELVE a validar lo guardado porque los props también llegan del JSON del
 * modelo, de un clonado o de una edición a mano.
 *
 * LOS DOS PROPS (nivel bloque, sin migración; `extractor.js` parte de un clon del store, así que
 * sobreviven a la edición inline, y `ai-merge.js` los conserva si la IA no los menciona):
 *   · `map_embed` → `data` del <object> del mapa. Ya existía (lo leía el bloque, nadie lo escribía).
 *   · `map_url`   → destino del botón «Abrir en Google Maps». Nuevo.
 * Sin ninguno de los dos el bloque se comporta EXACTAMENTE como antes (búsqueda por dirección).
 *
 * QUÉ INCRUSTAR — medido con capturas reales en Chrome (29-09-2026), no supuesto:
 *   · Identificador de ficha (`ftid` 0x…:0x…, el `!1s0x…:0x…` de los enlaces de escritorio) →
 *     `/maps/embed?pb=…!1s<ftid>` pinta la FICHA exacta (nombre, dirección, estrellas), aunque no
 *     haya coordenadas. Es el formato del «Insertar un mapa» oficial, reducido a lo mínimo.
 *   · `cid` (enlace del Perfil de Empresa, `maps.google.com/?cid=…`) → `?cid=…&output=embed`, ficha.
 *   · Coordenadas → chincheta exacta, sin ficha.
 *   · Texto (`q=` del enlace del móvil: «Nombre, dirección completa») → ficha exacta.
 *   · Descartado: «nombre + coordenadas» como texto de búsqueda. Llevó a OTRA sucursal de la misma
 *     cadena; y «nombre» con `ll=` no marca nada.
 *
 * ENLACES CORTOS (`maps.app.goo.gl/…`, `goo.gl/maps/…`) — lo que da «Compartir» hoy: no se pueden
 * incrustar ni el navegador puede seguir su redirección (CORS). Aquí se reconocen (estado 'corto')
 * y el panel pide al servidor la URL larga (views.py::api_maps_resolver →
 * core_services/maps_enlace.py), que luego se interpreta aquí como cualquier otra.
 */

// ESPEJO de `core_services/html_sanitizer.py::_MAPA_HOST_RE` (lo que el saneo de publicación deja
// pasar en un <object>). `tests/test_maps_enlace.py` compara los dos patrones POR TEXTO: si cambias
// uno, cambia el otro, o el editor aceptará mapas que la publicación borra (o al revés).
export const HOST_GOOGLE_RE = /^(?:www\.|maps\.)?google(?:\.[a-z]{2,3}){1,2}$/;

// ESPEJO de `core_services/maps_enlace.py::ACORTADORES` (el servidor solo sigue estos).
export const ACORTADORES = ['maps.app.goo.gl', 'goo.gl'];

/** Parámetros de rastreo de «Compartir» que se quitan del enlace del botón. */
const PARAMS_RASTREO = ['entry', 'g_ep', 'g_st', 'skid', 'lucs', 'shh', 'coh', 'hl'];

/** Tope de lo que se guarda como enlace del botón: uno de escritorio ronda los 300-700. */
export const URL_MAX = 2000;

const _FTID_RE = /^0x[0-9a-f]{1,16}:0x[0-9a-f]{1,16}$/i;
const _FTID_EN_DATA_RE = /!1s(0x[0-9a-f]{1,16}:0x[0-9a-f]{1,16})/i;
// `!3d<lat>!4d<lng>` = el LUGAR (en los enlaces de escritorio). `@lat,lng` es la CÁMARA: la vista
// del mapa, desplazada respecto al sitio por el panel lateral; solo vale como último recurso.
const _PIN_RE = /!3d(-?\d{1,2}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/;
const _CAMARA_RE = /@(-?\d{1,2}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/;
const _COORDS_RE = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

const MOTIVO_NO_ES_ENLACE = 'Eso no parece un enlace. Pega el enlace de tu negocio en Google Maps (botón «Compartir»).';
const MOTIVO_NO_ES_GOOGLE = 'Solo se admiten enlaces de Google Maps.';
const MOTIVO_SIN_LUGAR = 'Ese enlace de Google Maps no señala ningún lugar. Abre la ficha de tu negocio y copia el enlace de «Compartir».';
const MOTIVO_RUTA = 'Es un enlace de una ruta, no de un lugar. Abre la ficha de tu negocio y copia el enlace de «Compartir».';

function _decodificar(s) {
    try { return decodeURIComponent(s); } catch (e) { return s; }
}

/** Texto de un parámetro o segmento de Maps: '+' es espacio y lo demás va percent-encoded. */
function _textoMaps(s) {
    return _decodificar(String(s || '').replace(/\+/g, ' ')).replace(/\s+/g, ' ').trim();
}

function _coordsValidas(lat, lng) {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (!isFinite(la) || !isFinite(ln) || Math.abs(la) > 90 || Math.abs(ln) > 180) return null;
    return { lat: String(lat), lng: String(lng) };
}

/**
 * Saca la URL de lo pegado: el `src` de un <iframe> (código de «Insertar un mapa») o el texto tal
 * cual, con `https://` delante si se pegó sin esquema. Devuelve un objeto URL o null.
 */
function _extraerUrl(texto) {
    let v = String(texto || '').trim();
    if (!v) return null;
    if (/<iframe/i.test(v)) {
        const m = v.match(/\ssrc\s*=\s*["']([^"']+)["']/i);
        if (!m) return null;
        v = m[1].replace(/&amp;/g, '&').trim();
    }
    if (/^\/\//.test(v)) v = 'https:' + v;
    else if (!/^[a-z][a-z0-9+.-]*:/i.test(v)) v = 'https://' + v;
    let u;
    try { u = new URL(v); } catch (e) { return null; }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (u.username || u.password || u.port) return null;
    u.protocol = 'https:';
    u.hash = '';
    return u;
}

function _esAcortador(u) {
    const host = u.hostname.toLowerCase();
    if (host === 'maps.app.goo.gl') return u.pathname.length > 1;
    return host === 'goo.gl' && /^\/maps\/./.test(u.pathname);
}

/** ¿Es una página de MAPAS de Google? (google.xx/maps…, o maps.google.xx con cualquier ruta). */
function _esGoogleMaps(u) {
    const host = u.hostname.toLowerCase();
    if (!HOST_GOOGLE_RE.test(host)) return false;
    return host.indexOf('maps.') === 0 || /^\/maps(\/|$)/.test(u.pathname);
}

function _normalizada(valor, admiteCorto) {
    if (typeof valor !== 'string' || !valor.trim() || valor.length > URL_MAX) return '';
    if (/<iframe/i.test(valor)) return '';
    const u = _extraerUrl(valor);
    if (!u || !(_esGoogleMaps(u) || (admiteCorto && _esAcortador(u)))) return '';
    return u.href;
}

/**
 * Validación de lo GUARDADO, la usa el renderer. Devuelven la URL NORMALIZADA (https, sin
 * credenciales ni fragmento; `new URL` ya escapa comillas y `<`/`>`, así que se puede interpolar en
 * un atributo) o '' si no vale.
 *   · `embedMapsSeguro`  → `data` del <object>: solo páginas de Google Maps (un enlace corto no se
 *     puede incrustar y el saneo de publicación lo borraría).
 *   · `enlaceMapsSeguro` → `href` del botón: Google Maps o su acortador (abre la ficha en la app).
 */
export function embedMapsSeguro(valor) { return _normalizada(valor, false); }
export function enlaceMapsSeguro(valor) { return _normalizada(valor, true); }

/** URL del mapa incrustado de una ficha (formato de «Insertar un mapa», mínimo). */
export function embedDeFicha(ftid, coords) {
    const lat = coords ? coords.lat : '0';
    const lng = coords ? coords.lng : '0';
    return 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000!2d' + lng + '!3d' + lat
        + '!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s' + ftid.replace(':', '%3A') + '!2s!5e0';
}

function _embedClasico(q) {
    return 'https://maps.google.com/maps?q=' + encodeURIComponent(q) + '&z=16&output=embed';
}

function _embedDeCid(cid) {
    return 'https://maps.google.com/maps?cid=' + cid + '&z=16&output=embed';
}

/** Enlace a la ficha a partir de su identificador: la mitad derecha del ftid es el cid en hex. */
function _enlaceDeFtid(ftid) {
    try {
        return 'https://maps.google.com/?cid=' + BigInt(ftid.split(':')[1]).toString();
    } catch (e) {
        return '';
    }
}

function _enlaceDeCoords(c) {
    return 'https://www.google.com/maps/search/?api=1&query=' + c.lat + ',' + c.lng;
}

/** Segmento de la ruta que sigue a `/maps/<clave>/` ('place', 'search'), como texto. */
function _segmento(u, clave) {
    const partes = u.pathname.split('/');
    const i = partes.indexOf(clave);
    if (i < 0 || !partes[i + 1] || partes[i + 1].charAt(0) === '@' || /^data=/.test(partes[i + 1])) return '';
    return _textoMaps(partes[i + 1]);
}

/**
 * Interpreta lo que el cliente pegó en el panel.
 *
 * @returns {{estado:'vacio'}
 *         | {estado:'corto', url:string}                      (hay que resolverlo en el servidor)
 *         | {estado:'ok', embed:string, url:string, precision:'ficha'|'coordenadas'|'texto'}
 *         | {estado:'error', motivo:string}}
 *   `url` es el destino del botón «Abrir en Google Maps»: lo pegado si es un enlace normal (abre la
 *   ficha en la app del móvil), o uno derivado si se pegó el código de «Insertar un mapa».
 *   `precision` dice al panel qué va a marcar el mapa, para decírselo al cliente con palabras.
 */
export function interpretarEnlaceMaps(texto) {
    if (!String(texto || '').trim()) return { estado: 'vacio' };
    const u = _extraerUrl(texto);
    if (!u) return { estado: 'error', motivo: MOTIVO_NO_ES_ENLACE };
    if (_esAcortador(u)) return { estado: 'corto', url: u.href };
    if (!_esGoogleMaps(u)) return { estado: 'error', motivo: MOTIVO_NO_ES_GOOGLE };

    const crudo = _decodificar(u.href);
    const ftidParam = (u.searchParams.get('ftid') || '').trim();
    const ftidData = crudo.match(_FTID_EN_DATA_RE);
    const ftid = _FTID_RE.test(ftidParam) ? ftidParam : (ftidData ? ftidData[1] : '');
    const cid = (u.searchParams.get('cid') || '').trim();
    const pin = crudo.match(_PIN_RE);
    const q = _textoMaps(u.searchParams.get('q') || u.searchParams.get('query') || '');
    const qCoords = q.match(_COORDS_RE);
    const camara = u.pathname.match(_CAMARA_RE);

    // Código de «Insertar un mapa» (o su URL): ya es un mapa incrustable, se usa tal cual.
    if (/^\/maps\/embed(\/|$)/.test(u.pathname)) {
        if (!u.searchParams.get('pb') && !q) return { estado: 'error', motivo: MOTIVO_SIN_LUGAR };
        // En el pb del embed el orden es `!2d<lng>!3d<lat>` (el centro del mapa = el sitio).
        const centro = crudo.match(/!2d(-?\d{1,3}(?:\.\d+)?)!3d(-?\d{1,2}(?:\.\d+)?)/);
        const c = centro ? _coordsValidas(centro[2], centro[1]) : null;
        const enlace = ftid ? _enlaceDeFtid(ftid) : (c ? _enlaceDeCoords(c) : '');
        return { estado: 'ok', embed: u.href, url: enlace, precision: ftid ? 'ficha' : 'coordenadas' };
    }

    if (/^\/maps\/dir(\/|$)/.test(u.pathname)) return { estado: 'error', motivo: MOTIVO_RUTA };

    // El enlace del botón, sin el rastreo que añade «Compartir» (no cambia a dónde lleva).
    const limpio = new URL(u.href);
    PARAMS_RASTREO.forEach(function (k) { limpio.searchParams.delete(k); });
    const enlace = limpio.href.length <= URL_MAX ? limpio.href : '';
    const coordsPin = pin ? _coordsValidas(pin[1], pin[2]) : null;
    const coordsQ = qCoords ? _coordsValidas(qCoords[1], qCoords[2]) : null;
    const coordsCamara = camara ? _coordsValidas(camara[1], camara[2]) : null;

    if (ftid) {
        return { estado: 'ok', embed: embedDeFicha(ftid, coordsPin), precision: 'ficha',
                 url: enlace || _enlaceDeFtid(ftid) };
    }
    if (/^\d{1,20}$/.test(cid)) {
        return { estado: 'ok', embed: _embedDeCid(cid), precision: 'ficha',
                 url: enlace || ('https://maps.google.com/?cid=' + cid) };
    }
    const exactas = coordsPin || coordsQ;
    if (exactas) {
        return { estado: 'ok', embed: _embedClasico(exactas.lat + ',' + exactas.lng), precision: 'coordenadas',
                 url: enlace || _enlaceDeCoords(exactas) };
    }
    // Texto: el `q` del enlace del móvil («Nombre, dirección completa») o el nombre de la ruta
    // `/place/<nombre>` · `/search/<texto>`. Va ANTES que la cámara: en esas rutas `@lat,lng` es la
    // vista de los resultados, no el sitio.
    const busqueda = q || _segmento(u, 'place') || _segmento(u, 'search');
    if (busqueda) {
        return { estado: 'ok', embed: _embedClasico(busqueda), precision: 'texto', url: enlace };
    }
    if (coordsCamara) {
        return { estado: 'ok', embed: _embedClasico(coordsCamara.lat + ',' + coordsCamara.lng),
                 precision: 'coordenadas', url: enlace || _enlaceDeCoords(coordsCamara) };
    }
    return { estado: 'error', motivo: MOTIVO_SIN_LUGAR };
}
